import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

const challengeBannerVariants = cva('relative mb-10 flex flex-col justify-between gap-3 px-8 py-6 text-white', {
  variants: {
    variant: {
      default: ['min-h-[240px]', 'rounded-[20px]', 'bg-sky-600', 'bg-[radial-gradient(circle,_rgba(1,121,195,1)_0%,_rgba(0,43,106,1)_100%)]'],
      admin: [
        'min-h-fit',
        'rounded-[10px]',
        'border border-solid border-neutral-lightest',
        'bg-[linear-gradient(10deg,#FFF_20.02%,#F5F5F5_85.13%)]',
        'shadow-dropshadow-lg',
        '[&_.c-challenge-banner-bg]:bg-[right_10%_center] [&_.c-challenge-banner-header]:gap-2 [&_.c-challenge-banner-title]:text-primary-dark',
        '[&_.c-challenge-banner-date]:text-neutral-dark [&_.c-challenge-banner-date]:text-base [&_.c-challenge-banner-date]:font-light'
      ]
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const ChallengeBanner = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-banner', challengeBannerVariants({ variant, className }))}
    {...props}
  />
));
ChallengeBanner.displayName = 'ChallengeBanner';

const ChallengeBannerBg = React.forwardRef(({ img, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-banner-bg', 'pointer-events-none absolute inset-0 z-0 bg-no-repeat', 'bg-[center_right_-80px]', className)}
    style={{ backgroundImage: `url(${img})` }}
    {...props}
  />
));
ChallengeBannerBg.displayName = 'ChallengeBannerBg';

const ChallengeBannerHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-banner-header', 'relative z-[1] flex flex-col', className)}
    {...props}
  />
));
ChallengeBannerHeader.displayName = 'ChallengeBannerHeader';

const ChallengeBannerBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-banner-body', 'relative z-[1]', className)}
    {...props}
  />
));
ChallengeBannerBody.displayName = 'ChallengeBannerBody';

const ChallengeBannerFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-banner-footer', 'relative z-[1]', className)}
    {...props}
  />
));
ChallengeBannerFooter.displayName = 'ChallengeBannerFooter';

const ChallengeBannerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn('c-challenge-banner-title', 'mb-0 text-4xl font-extrabold leading-tight text-white', className)}
    {...props}
  />
));
ChallengeBannerTitle.displayName = 'ChallengeBannerTitle';

const ChallengeBannerDate = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-challenge-banner-date', 'mb-0 text-xs font-light text-white', className)}
    {...props}
  />
));
ChallengeBannerDate.displayName = 'ChallengeBannerDate';

const ChallengeBannerCount = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-challenge-banner-count', 'mb-0 inline-flex items-center gap-3 rounded-md bg-white/20 p-2 px-3 py-2 text-sm font-bold text-white [&>svg]:w-5', className)}
    {...props}
  />
));
ChallengeBannerCount.displayName = 'ChallengeBannerCount';

export { ChallengeBanner, ChallengeBannerBg, ChallengeBannerHeader, ChallengeBannerBody, ChallengeBannerFooter, ChallengeBannerTitle, ChallengeBannerDate, ChallengeBannerCount };
